import Vue, { CreateElement } from "vue";
import { Component, Watch } from "vue-property-decorator";
import {
    VApp,
    VSpacer,
    VToolbar,
    VToolbarTitle,
    VMain,
    VFooter,
    VSnackbar,
    VAppBar,
    VChip,
    VAvatar,
    VIcon,
    VBtn,
    VMenu,
    VList,
    VListItem,
} from "vuetify/lib";

import PageContainer from "@/components/page-container";
import AppBar from "@/components/app-bar";
import { APP_NAME, getAuthServiceUrl } from "@/appconfig";
import { setUserName, setAccessToken } from "@/store";
import { messages, TokenPayload } from "@/services";
import { UserIdPayload, getAzureAccessToken } from "@/oauth";
import { isMobileDisplay, throwIfNotNetworkError } from "./utils";
import { noop } from "@/components/search-input";
import { getUserFromAnyAuth, getAuthorizationToken } from "@/auth-helpers";

enum AvatarState {
    WAITING,
    OK,
    ERROR,
}

@Component({
    components: {
        PageContainer,
        VAppBar,
        VToolbar,
        VSnackbar,
        VSpacer,
        VToolbarTitle,
        VFooter,
        VApp,
        VChip,
        VAvatar,
        VIcon,
        VBtn,
        VMain,
        VMenu,
        VList,
        VListItem,
    },
})
export default class App extends Vue {
    userName: string;
    showError: boolean;
    errorMessage: string;
    avatarState: AvatarState = AvatarState.WAITING;
    loading: boolean;
    forbiddenError: boolean;
    notFoundError: boolean;
    notFoundMessage?: string;
    authServiceUrl?: string;
    displayMobile: boolean;
    tokenTimer: any;

    data() {
        return {
            userName: undefined,
            showError: false,
            noAvatar: false,
            errorMessage: "",
            loading: false,
            notFoundError: false,
            forbiddenError: false,
            notFoundMessage: undefined,
            authServiceUrl: getAuthServiceUrl(),
            displayMobile: isMobileDisplay(),
        };
    }

    // As the transformRequest() in map.tsx mapbox options doesn't allow async fuctions,
    //  we are setting the token in the state object and using a timer here to refresh it.
    // NOTE: Currently unused. Should be called from onLoggedIn() to keep the token fresh.
    setTokenRefresh() {
        if (!this.tokenTimer) {
            this.tokenTimer = setInterval(async () => {
                const token = await getAzureAccessToken();
                if (token) {
                    setAccessToken(this.$store, token);
                }
            }, 300000);
        }
    }

    // 1. When the app starts and it checks for an existing login
    // 2. After a new login the "login" event is emitted which is handled here
    async onLoggedIn(userData: UserIdPayload | TokenPayload) {
        this.userName = userData && userData.uid ? userData.uid : "";
        setUserName(this.$store, this.userName);

        if (this.$apm) {
            this.$apm.setUserContext({ username: this.userName });

            const rolesData = userData && userData.roles ? userData.roles : [];
            if (rolesData.length > 0) {
                const roles = Array.from(rolesData).sort().join(",");
                this.$apm.addLabels({ roles });
            }
        }

        try {
            const token = await getAuthorizationToken();
            if (token) {
                setAccessToken(this.$store, token);
            }
        } catch (error) {
            throwIfNotNetworkError(error);
        }
    }

    get appName() {
        return APP_NAME;
    }

    @Watch("$route")
    resetErrors() {
        this.notFoundMessage = undefined;
        this.notFoundError = false;
        this.forbiddenError = false;
        this.showError = false;
    }

    async mounted() {
        messages.$on("login", this.onLoggedIn);
        messages.$on("apiError", (message: string) => {
            this.errorMessage = message;
            this.showError = true;
        });
        messages.$on("notFoundError", () => {
            // catch: Vue Router rejects with "Navigation cancelled" if another navigation
            // (e.g. the beforeEach login redirect) interrupts this push. Safe to ignore
            // because the user will end up on the correct page either way.
            this.$router.push("/not-found").catch(noop);
        });
        messages.$on("unauthorized", () => {
            if (this.$route.path !== "/login") {
                this.userName = "";
                // catch: same as above — concurrent navigations (e.g. multiple 401 responses
                // firing this event) can cause "Navigation cancelled" or "Navigation duplicated"
                // rejections. The redirect to /login will still succeed.
                this.$router.push({ path: "/login", query: { redirect: this.$route.fullPath } }).catch(noop);
            }
        });
        messages.$on("forbidden", () => {
            this.forbiddenError = true;
        });

        const userData = await getUserFromAnyAuth();
        if (userData) {
            this.onLoggedIn(userData);
        }
    }

    get user() {
        const initials = this.userName
            .split(".")
            .map(word => word[0])
            .join("")
            .toUpperCase();
        return initials;
    }

    render(h: CreateElement) {
        return (
            <v-app>
                <v-app-bar fixed app extension-height="3" height="62" extended class="primary">
                    {!this.displayMobile && [
                        <img src="/Gigaclear_MasterLogo_white.svg" style="width: 10%; min-width: 120px" />,
                        <v-toolbar-title class="ml-2">{this.appName}</v-toolbar-title>,
                    ]}
                    <AppBar />
                    <v-spacer></v-spacer>
                    {this.userName && [
                        <v-menu
                            offset-y
                            scopedSlots={{
                                activator: ({ on }: any) => {
                                    if (this.displayMobile) {
                                        return (
                                            <v-btn icon color="white" onClick={on.click}>
                                                <v-avatar>
                                                    <v-icon>mdi-account-circle</v-icon>
                                                </v-avatar>
                                            </v-btn>
                                        );
                                    } else {
                                        return (
                                            <v-chip id="username" slot="activator" class="ml-1" onClick={on.click}>
                                                <v-avatar color="secondary" left>
                                                    <v-icon>mdi-account-circle</v-icon>
                                                </v-avatar>
                                                {this.user}
                                            </v-chip>
                                        );
                                    }
                                },
                            }}
                        ></v-menu>,
                    ]}
                </v-app-bar>
                <v-main>
                    <transition name="router" mode="out-in">
                        <PageContainer
                            forbiddenError={this.forbiddenError}
                            notFoundError={this.notFoundError}
                            notFoundMessage={this.notFoundMessage}
                        >
                            <router-view class="view router-view"></router-view>
                        </PageContainer>
                    </transition>
                    <v-snackbar bottom v-model={this.showError} timeout={60000}>
                        <span>{this.errorMessage}</span>
                        <v-btn text color="orange" onClick={() => (this.showError = false)}>
                            Close
                        </v-btn>
                    </v-snackbar>
                </v-main>
            </v-app>
        );
    }
}

// VUE JSX HOT LOADER //
if (module.hot) require("/src/node_modules/vue-jsx-hot-loader/src/api.js")({ Vue: require('vue'), ctx: __webpack_exports__, module: module, hotId: "_vue_jsx_hot-55ac039d/app.tsx" });